import {
  DocumentType,
  ExtractionStatus,
  WasteManifestIngestSource,
} from "generated-graphql/graphql";

/**
 * Default omnisearch for the Staff Manifest Documents page. Always restricts to
 * the structured RCRA manifest documents plus the EPA printed-copy scans
 * (`RCRAManifestPrintedCopy`) so staff can trigger a parse on the latter. The
 * page is cross-tenant; pass a `tenantId` to pre-scope it (e.g. a deep link
 * from an organization page). The `tenantId` term is smart-merged, so the
 * in-grid tenant picker still overrides it. The `documentType` enum filter
 * accepts comma-separated values (IN semantics).
 */
export function buildManifestDocumentsSearch(tenantId?: string | null): string {
  const docTypes = `documentType:${DocumentType.RcraManifest},${DocumentType.RcraManifestPrintedCopy}`;
  return tenantId ? `tenantId:${tenantId} ${docTypes}` : docTypes;
}

/**
 * Label for the per-row parse action. A never-parsed document (e.g. an ingested
 * printed copy at `NOT_ATTEMPTED`) is a first parse — "Parse with AI". Anything
 * already parsed is a "Re-parse".
 */
export function reparseMenuLabel(extractionStatus: ExtractionStatus | null | undefined): string {
  return extractionStatus === ExtractionStatus.NotAttempted ? "Parse with AI" : "Re-parse";
}

/** Display label for each `documentSourceKind()` result, shared by every grid that surfaces it. */
export const DOCUMENT_SOURCE_LABELS: Record<string, string> = {
  api: "API (RCRAInfo)",
  human: "User upload",
};

/**
 * Coarse provenance (API vs user upload) for the customer Manifest list view's
 * Source column, derived from the document type. RCRAManifestPrintedCopy is
 * pulled from EPA's RCRAInfo API ("api"); everything else is a user upload
 * ("human"). The staff observability grid uses the richer `manifestSourceBucket`
 * below instead.
 */
export function documentSourceKind(
  documentType: DocumentType | null | undefined,
  // The parse pipeline reclassifies a printed copy to RCRAManifest but preserves
  // the ingested type in originalDocumentType — check both so the source survives parse.
  originalDocumentType?: DocumentType | null
): "api" | "human" {
  const isPrintedCopy =
    documentType === DocumentType.RcraManifestPrintedCopy ||
    originalDocumentType === DocumentType.RcraManifestPrintedCopy;
  return isPrintedCopy ? "api" : "human";
}

/**
 * Provenance buckets for the grid's Source column. These are the tokens the
 * `documentSource` omnisearch filter emits; the resolver's `documentSource`
 * handler maps each back to a WHERE clause. Keep the two in sync.
 */
export const MANIFEST_SOURCE_BUCKETS = ["api", "public", "upload", "email", "csv"] as const;
export type ManifestSourceBucket = (typeof MANIFEST_SOURCE_BUCKETS)[number];

export const MANIFEST_SOURCE_LABELS: Record<ManifestSourceBucket, string> = {
  api: "API (RCRAInfo)",
  public: "EPA Public",
  upload: "User upload",
  email: "Email",
  csv: "CSV import",
};

/** WasteManifest.ingestSource values that resolve to each provenance bucket. The
 * resolver's `documentSource` filter groups the authoritative ingestSource by
 * these same sets — keep them in sync so the filter matches the displayed icon. */
export const INGEST_SOURCES_BY_BUCKET = {
  api: [
    WasteManifestIngestSource.RcraInfoApi,
    WasteManifestIngestSource.BrowserAutomation,
    WasteManifestIngestSource.RcraInfoAttachment,
  ],
  public: [WasteManifestIngestSource.Public],
  upload: [WasteManifestIngestSource.DocumentUpload],
  csv: [WasteManifestIngestSource.CsvImport],
} satisfies Partial<Record<ManifestSourceBucket, WasteManifestIngestSource[]>>;

export function isManifestSourceBucket(value: string): value is ManifestSourceBucket {
  return MANIFEST_SOURCE_BUCKETS.some((bucket) => bucket === value);
}

/** Human label for a source token, tolerating unknown tokens (returns them as-is). */
export function manifestSourceLabel(value: string): string {
  return isManifestSourceBucket(value) ? MANIFEST_SOURCE_LABELS[value] : value;
}

type ManifestSourceRow = {
  receivedEmailId?: string | null;
  documentType?: DocumentType | null;
  originalDocumentType?: DocumentType | null;
  sourceWasteManifest?: { ingestSource?: WasteManifestIngestSource | null } | null;
};

/**
 * Provenance of a manifest document for the grid's source indicator, keyed off
 * the authoritative `WasteManifest.ingestSource` (and the email signal on the
 * document). Falls back to the printed-copy document type for scans that have
 * not been parsed into a manifest yet (no `ingestSource` available), preserving
 * the pre-ingestSource behavior for those rows.
 */
export function manifestSourceBucket(row: ManifestSourceRow): ManifestSourceBucket {
  // Email wins over any ingest source (emailed docs share the DocumentUpload
  // ingest source). The resolver mirrors this by excluding emailed docs from
  // every non-email bucket.
  if (row.receivedEmailId) return "email";

  const ingestSource = row.sourceWasteManifest?.ingestSource;
  if (ingestSource != null) {
    for (const [bucket, sources] of Object.entries(INGEST_SOURCES_BY_BUCKET)) {
      if (sources.some((source) => source === ingestSource) && isManifestSourceBucket(bucket)) {
        return bucket;
      }
    }
  }

  // No linked manifest yet (e.g. an unparsed EPA printed-copy scan) or a legacy
  // manifest with a null ingestSource: treat a printed copy as API-sourced,
  // everything else as a user upload.
  const isPrintedCopy =
    row.documentType === DocumentType.RcraManifestPrintedCopy ||
    row.originalDocumentType === DocumentType.RcraManifestPrintedCopy;
  return isPrintedCopy ? "api" : "upload";
}

/** Manifest tracking number shape: 9 digits + 3 uppercase letters (e.g. 027720484JJK). */
const MTN_PATTERN = /\b\d{9}[A-Z]{3}\b/;

/**
 * Best-effort manifest tracking number derived from a document's storageLink, so
 * the parse override can force correct linkage instead of relying on the AI to
 * read the MTN. Printed-copy ingest stores the MTN in the key
 * (`epa-manifest-printed-copies/{tenant}/{mtn}.pdf`); this also tolerates a
 * date-suffixed filename. Returns null when no MTN-shaped token is found.
 *
 * POC: a persisted `Document.sourceManifestTrackingNumber` column should replace
 * this derivation once we're ready for the migration.
 */
export function mtnFromStorageLink(storageLink: string | null | undefined): string | null {
  if (!storageLink) return null;
  const basename = storageLink.split("/").pop() ?? storageLink;
  return basename.match(MTN_PATTERN)?.[0] ?? null;
}

type FacilityEpaIdStatus = {
  epaId: string;
  isInactive: boolean;
  isPrimaryForFacility: boolean;
};

/**
 * Generator EPA ID for the parse override: the primary, active EPA ID of the
 * document's first linked facility (falling back to any active EPA ID). Lets the
 * parse force the generator instead of leaving it to AI extraction. Returns null
 * when the facility has no usable EPA ID. A printed copy is linked to exactly one
 * facility at ingest, so the first facility is the generator.
 */
export function generatorEpaIdFromFacilities(
  facilities:
    | ReadonlyArray<{ epaIdStatuses?: readonly FacilityEpaIdStatus[] | null }>
    | null
    | undefined
): string | null {
  const statuses = facilities?.[0]?.epaIdStatuses ?? [];
  return (
    statuses.find((s) => s.isPrimaryForFacility && !s.isInactive)?.epaId ??
    statuses.find((s) => !s.isInactive)?.epaId ??
    null
  );
}
